/**
 * useViewportVirtualization Hook
 *
 * Provides viewport-based virtualization for the flowchart.
 * Only nodes within the visible area + padding are rendered.
 *
 * PERFORMANCE: Uses refs for pan/zoom to avoid stale closures,
 * since pan/zoom are updated via direct DOM manipulation.
 *
 * IMPORTANT: Do not assign panRef/zoomRef from props on every render — minimap / fast-pan
 * update the canvas transform and dispatch `panupdate` without React state until the gesture
 * ends. A render in between would overwrite live refs with stale props and cull every node.
 *
 * Padding: one extra screen of real cards stays mounted on every side. A slightly
 * looser band keeps a card until it clears that edge, so the boundary does not flicker.
 * New full cards mount a few per frame after a resize or data change.
 * A live pan only moves the canvas. Cards already up stay up. Newcomers mount
 * one at a time on a later turn, so a late card does not hold the pan frame.
 *
 * EDGE VISIBILITY: An edge stays if either endpoint is a full on-screen card, or if the
 * box spanning both endpoints crosses the screen. That covers wires that pass through
 * the view while both cards sit outside it, without parsing SVG path strings.
 */

import { useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { EDGE_CULL_PADDING_RATIO } from './constants';
import { calculateVisibleBounds, endpointBoxTouchesBounds } from './geometry';
import { useVisibleNodeSet } from './useVisibleNodeSet';
import { useVirtualizationEffects } from './useVirtualizationEffects';

export function useViewportVirtualization({
  tasks,
  positionsToUse,
  nodeDimensions,
  containerRef,
  pan,
  zoom,
  // Nodes that should always be rendered regardless of visibility
  selectedNodes = new Set(),
  draggingNode = null,
  connectingFrom = null,
  hoveredNodeId = null,
  // Animation state - disable culling during animations
  isAnimatingLayout = false,
  isAnimatingIntoFocus = false,
  isAnimatingOutOfFocus = false,
}) {
  // Use refs for pan/zoom — `panupdate` writes live values during minimap / trackpad pan.
  // Sync from React props only when pan/zoom actually change (e.g. after gesture commits).
  const panRef = useRef(pan);
  const zoomRef = useRef(zoom);
  useLayoutEffect(() => {
    panRef.current = pan;
    zoomRef.current = zoom;
  }, [pan, zoom]);

  /** Viewport size for culling — updated by ResizeObserver, not on every pan (avoids layout thrash). */
  const containerRectRef = useRef(null);
  const visibleBoundsRef = useRef(null);

  const {
    visibleNodeIds,
    fullNodeIds,
    updateVisibility,
    forceUpdate,
    startInteraction,
    endInteraction,
    setGesturing,
    followCamera,
  } = useVisibleNodeSet({
    tasks,
    positionsToUse,
    nodeDimensions,
    containerRef,
    panRef,
    zoomRef,
    containerRectRef,
    visibleBoundsRef,
    selectedNodes,
    draggingNode,
    connectingFrom,
    hoveredNodeId,
    isAnimatingLayout,
    isAnimatingIntoFocus,
    isAnimatingOutOfFocus,
  });

  useVirtualizationEffects({
    containerRef,
    updateVisibility,
    tasks,
    positionsToUse,
    panRef,
    zoomRef,
    containerRectRef,
    visibleBoundsRef,
    setGesturing,
    followCamera,
  });

  const isNodeVisible = useCallback((taskId) => {
    return visibleNodeIds.has(taskId);
  }, [visibleNodeIds]);

  const visibleTasks = useMemo(() => {
    return tasks.filter((task) => visibleNodeIds.has(task.id));
  }, [tasks, visibleNodeIds]);

  /**
   * Keep a wire when an on-screen card owns it, or when the span between the
   * two endpoints crosses the screen (long wires whose cards are both outside).
   */
  const filterVisibleEdges = useCallback((edges) => {
    const rect = containerRectRef.current;
    const bounds = rect
      ? calculateVisibleBounds(rect, panRef.current, zoomRef.current, EDGE_CULL_PADDING_RATIO)
      : null;

    return edges.filter((edge) => {
      if (fullNodeIds.has(edge.from) || fullNodeIds.has(edge.to)) return true;
      return endpointBoxTouchesBounds(edge, bounds);
    });
  }, [fullNodeIds, containerRectRef, panRef, zoomRef]);

  const stats = useMemo(() => ({
    total: tasks.length,
    visible: visibleNodeIds.size,
    culled: tasks.length - visibleNodeIds.size,
  }), [tasks.length, visibleNodeIds.size]);

  return {
    visibleNodeIds,
    fullNodeIds,
    visibleTasks,
    isNodeVisible,
    filterVisibleEdges,
    updateVisibility,
    forceUpdate,
    startInteraction,
    endInteraction,
    stats,
  };
}

export default useViewportVirtualization;
